import { useState } from 'react';
import { Button, TextInput } from '@c2fo/liquidity';
import { AngleDownIcon, AngleRightIcon } from '@c2fo/liquidity/icons';
import { createScheduledDelivery } from '../../api/ScheduledDeliveries';
import { ScheduleType, type PreDeliveryAction, type PostDeliveryAction, type RetryConfig } from '../../api/shared';
import { useApiContext } from '../../contexts/FileDeliveryContext';
import { LocationSelector } from '../LocationSelector';
import { ScheduleEditor } from '../ScheduleEditor';
import { DeliveryActionsEditor } from '../DeliveryActionsEditor';
import { RetryConfigEditor } from '../RetryConfigEditor';
import type { ScheduledDelivery } from '../../api/ScheduledDeliveries/types';
import { FormField } from '../FormField';
import { Modal } from '../Modal';

interface NewScheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (schedule: ScheduledDelivery) => void;
  onError: (message: string) => void;
}

export const NewScheduleModal: React.FC<NewScheduleModalProps> = ({ isOpen, onClose, onCreated, onError }) => {
  const apiContext = useApiContext();
  const [displayName, setDisplayName] = useState('');
  const [sourceLocation, setSourceLocation] = useState('');
  const [sourcePath, setSourcePath] = useState('');
  const [targetLocation, setTargetLocation] = useState('');
  const [targetPath, setTargetPath] = useState('');
  const [scheduleType, setScheduleType] = useState<ScheduleType>(ScheduleType.SCHEDULE_TYPE_CRON);
  const [scheduleExpression, setScheduleExpression] = useState('');
  const [scheduleTimezone, setScheduleTimezone] = useState('UTC');
  const [ignoreEmptySource, setIgnoreEmptySource] = useState(false);
  const [preDeliveryAction, setPreDeliveryAction] = useState<PreDeliveryAction | undefined>(undefined);
  const [postDeliveryAction, setPostDeliveryAction] = useState<PostDeliveryAction | undefined>(undefined);
  const [retryConfig, setRetryConfig] = useState<RetryConfig | undefined>(undefined);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const isFormValid = () => {
    return sourceLocation && sourcePath.trim() && targetLocation && targetPath.trim() && scheduleExpression.trim();
  };

  const handleSave = async () => {
    if (!sourceLocation || !sourcePath.trim() || !targetLocation || !targetPath.trim() || !scheduleExpression.trim()) {
      onError('Please fill in all required fields');
      return;
    }

    setIsSaving(true);
    try {
      const schedule = await createScheduledDelivery(apiContext, {
        scheduledDelivery: {
          name: '',
          displayName: displayName.trim() || undefined,
          sourceLocation: `locations/${sourceLocation}`,
          sourcePath: sourcePath.trim(),
          targetLocation: `locations/${targetLocation}`,
          targetPath: targetPath.trim(),
          schedule: {
            type: scheduleType,
            expression: scheduleExpression.trim(),
            timezone: scheduleTimezone.trim() || 'UTC',
          },
          ignoreEmptySource,
          preDeliveryAction,
          postDeliveryAction,
          retryConfig,
        },
      });

      onCreated(schedule);
      handleClose();
    } catch {
      onError('Failed to create scheduled delivery');
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setDisplayName('');
    setSourceLocation('');
    setSourcePath('');
    setTargetLocation('');
    setTargetPath('');
    setScheduleType(ScheduleType.SCHEDULE_TYPE_CRON);
    setScheduleExpression('');
    setScheduleTimezone('UTC');
    setIgnoreEmptySource(false);
    setPreDeliveryAction(undefined);
    setPostDeliveryAction(undefined);
    setRetryConfig(undefined);
    setShowAdvanced(false);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="New Scheduled Delivery"
      footer={
        <>
          <Button variant="tertiary" className="rounded-md" onClick={handleClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            className="rounded-md text-white"
            onClick={handleSave}
            disabled={isSaving || !isFormValid()}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </>
      }
    >
      <FormField label="Display Name">
        <TextInput
          value={displayName}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDisplayName(e.target.value)}
          placeholder="Enter a name for this schedule"
          className="w-full"
          size="sm"
        />
      </FormField>

      <div className="grid grid-cols-2 gap-6">
        <FormField label="Source Location" required>
          <LocationSelector
            value={sourceLocation}
            onChange={setSourceLocation}
            onError={onError}
            placeholder="Select source location"
          />
        </FormField>
        <FormField label="Source Path" required>
          <TextInput
            value={sourcePath}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSourcePath(e.target.value)}
            placeholder="e.g., /path/to/files/*.csv"
            className="w-full font-mono"
            size="sm"
          />
          <p className="mt-1 text-xs text-gray-500">Supports glob patterns</p>
        </FormField>
      </div>

      <div className="grid grid-cols-2 gap-6">
        <FormField label="Target Location" required>
          <LocationSelector
            value={targetLocation}
            onChange={setTargetLocation}
            onError={onError}
            placeholder="Select target location"
          />
        </FormField>
        <FormField label="Target Path" required>
          <TextInput
            value={targetPath}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTargetPath(e.target.value)}
            placeholder="e.g., /destination/folder/"
            className="w-full font-mono"
            size="sm"
          />
        </FormField>
      </div>

      <ScheduleEditor
        scheduleType={scheduleType}
        expression={scheduleExpression}
        timezone={scheduleTimezone}
        onTypeChange={setScheduleType}
        onExpressionChange={setScheduleExpression}
        onTimezoneChange={setScheduleTimezone}
      />

      <div className="flex items-center">
        <input
          type="checkbox"
          id="ignoreEmptySourceNew"
          checked={ignoreEmptySource}
          onChange={(e) => setIgnoreEmptySource(e.target.checked)}
          className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />
        <label htmlFor="ignoreEmptySourceNew" className="ml-2 text-sm text-gray-700">
          Ignore empty source
        </label>
      </div>

      <button
        type="button"
        onClick={() => setShowAdvanced(!showAdvanced)}
        className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium"
      >
        {showAdvanced ? <AngleDownIcon className="h-4 w-4" /> : <AngleRightIcon className="h-4 w-4" />}
        Advanced Settings
      </button>

      {showAdvanced && (
        <div className="space-y-6 pl-5 border-l-2 border-gray-200">
          <DeliveryActionsEditor
            preDeliveryAction={preDeliveryAction}
            postDeliveryAction={postDeliveryAction}
            onPreDeliveryActionChange={setPreDeliveryAction}
            onPostDeliveryActionChange={setPostDeliveryAction}
          />

          <RetryConfigEditor retryConfig={retryConfig} onChange={setRetryConfig} />
        </div>
      )}
    </Modal>
  );
};
